/**
 * Servizio API per gestione utenti e ruoli
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

export interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  roleId?: string;
  roleName?: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  permissions: string[];
  menuPermissions?: string[]; // Permessi specifici per le voci di menu
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDto {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  roleId: string;
}

export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  roleId?: string;
  isActive?: boolean;
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  permissions: string[];
  menuPermissions?: string[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  permissions?: string[];
  menuPermissions?: string[];
  isActive?: boolean;
}

class UsersApiService {
  private getHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };
  }

  // ==================== USERS ====================

  async getUsers(): Promise<User[]> {
    const response = await axios.get(`${API_BASE_URL}/users`, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await axios.get(`${API_BASE_URL}/users/${id}`, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async createUser(userData: CreateUserDto): Promise<User> {
    const response = await axios.post(`${API_BASE_URL}/users`, userData, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async updateUser(id: string, userData: UpdateUserDto): Promise<User> {
    const response = await axios.patch(`${API_BASE_URL}/users/${id}`, userData, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/users/${id}`, {
      headers: this.getHeaders(),
    });
  }

  async reactivateUser(id: string): Promise<User> {
    const response = await axios.post(`${API_BASE_URL}/users/${id}/reactivate`, {}, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async changeUserPassword(id: string, newPassword: string): Promise<void> {
    await axios.patch(`${API_BASE_URL}/users/${id}/password`, { newPassword }, {
      headers: this.getHeaders(),
    });
  }

  async getUserStats(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/users/stats`, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  // ==================== ROLES ====================

  async getRoles(): Promise<Role[]> {
    const response = await axios.get(`${API_BASE_URL}/roles`, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async getRole(id: string): Promise<Role> {
    const response = await axios.get(`${API_BASE_URL}/roles/${id}`, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async createRole(roleData: CreateRoleDto): Promise<Role> {
    const response = await axios.post(`${API_BASE_URL}/roles`, roleData, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async updateRole(id: string, roleData: UpdateRoleDto): Promise<Role> {
    const response = await axios.patch(`${API_BASE_URL}/roles/${id}`, roleData, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/roles/${id}`, {
      headers: this.getHeaders(),
    });
  }

  async cloneRole(id: string, newName: string): Promise<Role> {
    const response = await axios.post(`${API_BASE_URL}/roles/${id}/clone`, { newName }, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async getRoleUsers(id: string): Promise<User[]> {
    const response = await axios.get(`${API_BASE_URL}/roles/${id}/users`, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  async getRoleStats(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/roles/stats`, {
      headers: this.getHeaders(),
    });
    return response.data.data || response.data;
  }

  // ==================== PERMISSIONS ====================

  async getAvailablePermissions(): Promise<string[]> {
    // Lista completa dei permessi disponibili nel sistema
    return [
      'read:users',
      'create:users',
      'update:users',
      'delete:users',
      'read:roles',
      'create:roles',
      'update:roles',
      'delete:roles',
      'read:dashboard',
      'read:integration',
      'write:integration',
      'read:bookings',
      'create:bookings',
      'update:bookings',
      'delete:bookings',
      'read:prescriptions',
      'read:audit',
      'impersonate:users',
    ];
  }

  async getAvailableMenuItems(): Promise<{ id: string; label: string; parent?: string }[]> {
    // Lista completa delle voci di menu disponibili
    return [
      { id: 'calendar', label: 'Calendario' },
      { id: 'dashboard', label: 'Dashboard' },
      { id: 'integration', label: 'Integrazione' },
      { id: 'syncData', label: 'Sync Dati di Base' },
      { id: 'hierarchicalStructure', label: 'Struttura Gerarchica', parent: 'syncData' },
      { id: 'authorities', label: 'Authorities', parent: 'syncData' },
      { id: 'sites', label: 'Sites', parent: 'syncData' },
      { id: 'hospitals', label: 'Hospitals', parent: 'syncData' },
      { id: 'operationUnits', label: 'Operation Units', parent: 'syncData' },
      { id: 'executionUnits', label: 'Execution Units', parent: 'syncData' },
      { id: 'healthcareServices', label: 'Healthcare Services', parent: 'syncData' },
      { id: 'doctors', label: 'Doctors', parent: 'syncData' },
      { id: 'diaries', label: 'Diaries', parent: 'syncData' },
      { id: 'diarySchemas', label: 'Diary Schemas', parent: 'syncData' },
      { id: 'diaryFormData', label: 'Diary Form Data', parent: 'syncData' },
      { id: 'prescriptions', label: 'Prescrizioni' },
      { id: 'recupProfiles', label: 'Profili RECUP' },
      { id: 'recupTests', label: 'Test RECUP' },
      { id: 'testEndpoints', label: 'Test Endpoints' },
      { id: 'databaseMaintenance', label: 'Database Maintenance' },
      { id: 'users', label: 'Utenti' },
    ];
  }
}

export default new UsersApiService();
